import { useEffect } from "react";

import { Main } from "@edclub/player";
import { type AppRouter } from "@edclub/shared-types";
import { cn } from "@edclub/utils";

import { AnimatedGiraffe, Clues, Crossword, HintsButton } from "./components";
import { ChangeImageSizeButton } from "./components/ChangeImageSizeButton";
import { CheckButton } from "./components/CheckButton";
import { type StateUpdate, useCrosswordStore } from "./store";

import "./App.scss";

export default function App(props: {
  lesson: AppRouter<StateUpdate>["lesson"];
}) {
  const setStore = useCrosswordStore.use.setStore();
  const useImagesIfAvailable = useCrosswordStore.use.useImagesIfAvailable();

  useEffect(() => {
    setStore(props.lesson.activity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="crossword-app flex max-w-screen flex-col">
      <Main
        className="flex flex-1 items-stretch overflow-hidden"
        aria-label={t("Crossword Puzzle Application")}
      >
        <aside
          className="flex w-full flex-col justify-between gap-2 overflow-auto p-2 md:w-1/3 lg:w-1/3"
          aria-label={t("Puzzle Controls and Clues")}
        >
          <header>
            <h1 className="m-0 h-10 text-4xl font-bold text-[#42576e] md:text-2xl">
              {t("Crossword Puzzle")}
            </h1>
          </header>
          <nav
            className="flex h-10 items-center gap-2"
            aria-label={t("Puzzle Actions")}
          >
            <ChangeImageSizeButton />
            <HintsButton />
          </nav>
          <section
            className="flex flex-1 overflow-auto rounded-md px-2 py-2"
            aria-labelledby="clues-heading"
          >
            <h2 id="clues-heading" className="sr-only">
              {t("Puzzle Clues")}
            </h2>
            <Clues />
          </section>
        </aside>
        <section
          className={cn(
            "relative h-full max-h-full max-w-full p-2 md:w-2/3",
            useImagesIfAvailable ? "lg:w-3/4" : "lg:w-2/3",
          )}
          aria-label={t("Crossword Grid")}
        >
          <AnimatedGiraffe aria-hidden="true" />
          <Crossword />
        </section>
        <CheckButton />
      </Main>
    </div>
  );
}
